import { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { useCommonContext } from "../../../utils/context";

const StockModal = ({
  show,
  handleClose,
  currentStock,
  onAdd,
  mode = "add",
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    item: "",
    description: "",
    quantity: "",
  });
  const [errors, setErrors] = useState({});
  const { fetchStocks } = useCommonContext();

  useEffect(() => {
    if (currentStock) {
      if (mode === "edit") {
        setFormData({
          item: currentStock.item || "",
          description: currentStock.description || "",
          quantity: currentStock.quantity || "",
        });
      } else if (mode === "addQuantity") {
        setFormData({
          item: currentStock.item || "",
          description: currentStock.description || "",
          quantity: "",
        });
      }
    } else {
      resetForm();
    }
  }, [currentStock, show, mode]);

  const resetForm = () => {
    setFormData({ item: "", description: "", quantity: "" });
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (mode === "add" || mode === "edit") {
      if (!formData.item.trim()) newErrors.item = "Item name is required.";
      if (!formData.description.trim())
        newErrors.description = "Description is required.";
    }
    if (
      !formData.quantity ||
      isNaN(formData.quantity) ||
      Number(formData.quantity) <= 0
    )
      newErrors.quantity = "Enter a valid quantity.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    let payload = {};
    if (mode === "add") {
      payload = {
        item: formData.item,
        description: formData.description,
        quantity: Number(formData.quantity),
      };
    } else if (mode === "edit") {
      payload = {
        item: formData.item,
        description: formData.description,
        quantity: Number(formData.quantity),
      };
    } else if (mode === "addQuantity") {
      payload = {
        new_quantity: Number(formData.quantity),
      };
    }

    try {
      let response;
      if (mode === "add") {
        response = await axios.post(`${BASE_URL}/api/inventory/`, payload, {
          headers: HEADERS,
        });
      } else {
        response = await axios.put(
          `${BASE_URL}/api/inventory/${currentStock.id}/`,
          payload,
          { headers: HEADERS },
        );
      }

      toast.success(response.data.message || "Stock saved successfully");
      onAdd();
      fetchStocks();
      handleClose();
      resetForm();
    } catch (err) {
      const errorData = err.response?.data;

      if (errorData) {
        Object.values(errorData).forEach((messages) => {
          messages.forEach((msg) => {
            toast.error(msg);
          });
        });
      } else {
        toast.error("Error saving stock. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === "edit"
            ? "Edit Stock"
            : mode === "addQuantity"
              ? "Add Quantity"
              : "Add Stock"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {(mode === "add" || mode === "edit") && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Item Name</Form.Label>
                <Form.Control
                  type="text"
                  name="item"
                  placeholder="Enter item name"
                  value={formData.item}
                  onChange={handleChange}
                  isInvalid={!!errors.item}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.item}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  name="description"
                  placeholder="Enter description"
                  value={formData.description}
                  onChange={handleChange}
                  isInvalid={!!errors.description}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description}
                </Form.Control.Feedback>
              </Form.Group>
            </>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              {mode === "addQuantity" ? "Add Quantity" : "Quantity"}
            </Form.Label>
            <Form.Control
              type="number"
              name="quantity"
              placeholder={
                mode === "addQuantity"
                  ? "Enter quantity to add"
                  : "Enter quantity"
              }
              value={formData.quantity}
              onChange={handleChange}
              isInvalid={!!errors.quantity}
            />
            <Form.Control.Feedback type="invalid">
              {errors.quantity}
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default StockModal;
